import React, { useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import Login from '../../views/loginOrRegister/Login.jsx';
import Signup from '../../views/loginOrRegister/Signup.jsx';
import Register from '../../views/LandingPage/FlexOfficeDomain/components/Register.jsx';
import { getTemplateAccountRoute, useTemplateAuthState } from '../TemplateAuthState.jsx';
import { isLoginTransitionActive } from '../../utils/loginTransition.jsx';

const contentTitle = (content = {}, pageKey = 'signIn') => (
  content.title
  || [content.titlePrefix, content.titleAccent, content.titleSuffix].filter(Boolean).join(' ')
  || (pageKey === 'signUp' ? 'Create your account' : 'Sign in to your account')
);

const getAuthComponent = (domainType, pageKey) => {
  if (pageKey === 'signUp' && domainType === 'FLEX_OFFICE') return Register;
  if (pageKey === 'signUp') return Signup;
  return Login;
};

const domainLabel = {
  EDUCATION: 'Academy',
  RETAIL: 'Store',
  FLEX_OFFICE: 'Workspace',
  WAAS: 'Technology'
};

const consoleRoles = new Set(['ADMIN', 'INSTRUCTOR', 'TEACHING_ASSISTANT', 'SUPER_ADMIN']);
const siteRoles = new Set(['CUSTOMER', 'STUDENT', 'APPLICANT', 'CANDIDATE', 'EMPLOYEE']);

const roleKey = (role = '') => String(role || '').trim().toUpperCase();
const resolveConsoleManageUrl = () => {
  if (typeof window === 'undefined') return '/console/manage';
  const currentOrigin = window.location.origin;
  if (currentOrigin.includes(':3000')) {
    return `${currentOrigin.replace(':3000', ':4000')}/console/manage`;
  }
  return '/console/manage';
};

const RedirectingAuthReader = ({ theme = {}, message = 'Preparing your account...' }) => (
  <section
    data-template-section="auth-redirect"
    style={{
      minHeight: 'calc(100vh - 80px)',
      display: 'grid',
      placeItems: 'center',
      padding: '72px 24px',
      background: theme.background || '#ffffff',
      color: theme.color || '#1f2937',
      fontFamily: theme.fontFamily || 'Inter, Arial, sans-serif'
    }}
  >
    <div style={{ textAlign: 'center', display: 'grid', gap: 10 }}>
      <div style={{ fontWeight: 800, fontSize: 18 }}>{message}</div>
      <div style={{ color: theme.mutedColor || '#64748b', fontSize: 14 }}>Redirecting...</div>
    </div>
  </section>
);

const isFlexOfficeSignUpEntrySection = (section = {}) => {
  const componentKey = section.content?.componentKey || section.bindings?.componentKey;
  const previewKey = section.content?.previewKey || section.bindings?.previewKey;
  return section.sectionId === 'flex-office-sign-up'
    || previewKey === 'flexOfficeSignUp'
    || ['Register', 'Signup'].includes(componentKey)
    || (
      section.type === 'flexOfficeCta'
      && section.content?.primaryActionUrl === '/sign-up'
      && /create your account|workspace account/i.test(`${section.content?.titlePrefix || ''} ${section.content?.title || ''}`)
    );
};

const AuthPageReader = ({
  domainType,
  pageKey,
  page,
  sections = [],
  theme = {},
  template,
  renderSection,
  educationData
}) => {
  const auth = useTemplateAuthState();
  const visibleSections = sections.filter((item) => item?.visible !== false);
  const section = visibleSections[0] || {};
  const remainingSections = visibleSections.slice(1);
  const content = section.content || {};
  const AuthComponent = getAuthComponent(domainType, pageKey);
  const label = domainLabel[domainType] || 'Account';
  const templateKey = template?.baseTemplateKey
    || template?.attributes?.sourceTemplate
    || template?.attributes?.retailTemplateStyle;
  const authPanelGradient = domainType === 'RETAIL' && templateKey === 'AvikarTemplate'
    ? 'linear-gradient(135deg, #f4a649 0%, #df8f32 100%)'
    : `linear-gradient(135deg, ${theme.secondary}, ${theme.primary})`;
  const title = contentTitle(content, pageKey);
  const subtitle = content.subtitle || content.body || `Continue with your ${label.toLowerCase()} account.`;
  const isFlexOfficeSignUp = domainType === 'FLEX_OFFICE' && pageKey === 'signUp';
  const isTemplatePreview = typeof window !== 'undefined' && window.location.pathname === '/template-preview';
  const normalizedRoles = (auth.roles || []).map(roleKey).filter(Boolean);
  const hasConsoleRole = normalizedRoles.some((role) => consoleRoles.has(role));
  const hasSiteRole = normalizedRoles.some((role) => siteRoles.has(role));
  const hasPendingLoginChoice = !isTemplatePreview && isLoginTransitionActive(['role-selection', 'employee-choice']);
  const shouldBypassAuthPage = !hasPendingLoginChoice && !isTemplatePreview && auth.isLoggedIn && ['signIn', 'signUp'].includes(pageKey);
  const shouldRedirectToConsole = shouldBypassAuthPage && hasConsoleRole && !hasSiteRole;
  const accountRoute = getTemplateAccountRoute(domainType);
  const isSignUpPage = pageKey === 'signUp';
  const formShellClassName = [
    'template-auth-reader-form-shell',
    isSignUpPage ? 'template-auth-reader-sign-up' : '',
    domainType === 'RETAIL' ? 'template-auth-reader-retail' : ''
  ].filter(Boolean).join(' ');
  const defaultTopPadding = 'calc(var(--layout-header-height, 70px) + 48px)';
  const authTopPadding = section.style?.paddingTop
    ? `max(${section.style.paddingTop}, ${defaultTopPadding})`
    : defaultTopPadding;

  useEffect(() => {
    if (!shouldRedirectToConsole) return;
    window.location.href = resolveConsoleManageUrl();
  }, [shouldRedirectToConsole]);

  if (shouldRedirectToConsole) {
    return <RedirectingAuthReader theme={theme} message="Opening console access..." />;
  }

  if (shouldBypassAuthPage) {
    return <Navigate to={accountRoute} replace />;
  }

  if (isFlexOfficeSignUp) {
    const remainingFlexSections = visibleSections.filter((item) => !isFlexOfficeSignUpEntrySection(item));
    return (
      <>
        <AuthComponent templateDomainType={domainType} />
        {typeof renderSection === 'function'
          ? remainingFlexSections.map((item, index) => renderSection(item, index + 1, template, educationData))
          : null}
      </>
    );
  }

  return (
    <>
      <section
        data-template-section={section.type || 'auth'}
        style={{
          minHeight: 'calc(100vh - 80px)',
          display: 'grid',
          placeItems: 'center',
          padding: section.style?.padding || '48px 24px 72px',
          paddingTop: authTopPadding,
          background: section.style?.backgroundColor || theme.background,
          color: theme.color,
          fontFamily: theme.fontFamily,
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
          overflowX: 'hidden'
        }}
      >
        <div
          className="template-auth-reader-grid"
          style={{
            width: '100%',
            maxWidth: theme.pageWidth || 1180,
            display: 'grid',
            gridTemplateColumns: 'minmax(0, .9fr) minmax(min(100%, 420px), 1.1fr)',
            gap: 32,
            alignItems: 'stretch',
            minWidth: 0
          }}
        >
          <aside
            style={{
              minWidth: 0,
              borderRadius: theme.cardRadius,
              padding: 'clamp(28px, 5vw, 56px)',
              color: '#ffffff',
              background: authPanelGradient,
              boxShadow: theme.cardShadow,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              minHeight: 420
            }}
          >
            <div>
              <span style={{ display: 'inline-flex', border: '1px solid rgba(255,255,255,.32)', borderRadius: 999, padding: '7px 12px', fontSize: 12, fontWeight: 800, letterSpacing: '.08em', textTransform: 'uppercase' }}>
                {content.eyebrow || 'Account Access'}
              </span>
              <h1 style={{ margin: '28px 0 16px', fontFamily: theme.headingFontFamily, fontSize: 'clamp(34px, 5vw, 58px)', lineHeight: 1.04 }}>
                {title}
              </h1>
              <p style={{ margin: 0, fontSize: 17, lineHeight: 1.7, color: 'rgba(255,255,255,.86)' }}>{subtitle}</p>
            </div>
            <div style={{ marginTop: 42, borderRadius: 18, background: 'rgba(255,255,255,.13)', padding: 18, border: '1px solid rgba(255,255,255,.18)' }}>
              <strong>{page?.title || label}</strong>
              <p style={{ margin: '6px 0 0', color: 'rgba(255,255,255,.78)' }}>
                Secure access for saved activity, orders, bookings, and profile details.
              </p>
            </div>
          </aside>
          <div
            className={formShellClassName}
            style={{
              minWidth: 0,
              width: '100%',
              maxWidth: '100%',
              borderRadius: theme.cardRadius,
              background: theme.surface,
              border: `1px solid ${theme.borderColor}`,
              boxShadow: theme.cardShadow,
              overflow: 'hidden'
            }}
          >
            <AuthComponent templateDomainType={domainType} />
          </div>
        </div>
        <style>{`
          .template-auth-reader-grid > * {
            min-width: 0;
          }
          .template-auth-reader-form-shell > div[class*="min-h-screen"] {
            min-height: auto !important;
            width: 100% !important;
            max-width: 100% !important;
            margin-top: 0 !important;
            padding: clamp(18px, 4vw, 32px) !important;
            background: transparent !important;
            box-sizing: border-box !important;
          }
          .template-auth-reader-form-shell > div[class*="min-h-screen"] > div[class*="max-w-7xl"] {
            width: 100% !important;
            max-width: 100% !important;
            grid-template-columns: minmax(0, 1fr) !important;
            box-shadow: none !important;
          }
          .template-auth-reader-form-shell > div[class*="min-h-screen"] > div[class*="max-w-7xl"] > div:first-child {
            display: none !important;
          }
          .template-auth-reader-form-shell > .max-w-md {
            max-width: 100% !important;
            width: 100% !important;
            margin: 0 !important;
            box-shadow: none !important;
          }
          .template-auth-reader-form-shell form,
          .template-auth-reader-form-shell form > div,
          .template-auth-reader-form-shell input,
          .template-auth-reader-form-shell select,
          .template-auth-reader-form-shell textarea,
          .template-auth-reader-form-shell button {
            width: 100%;
            max-width: 100%;
            min-width: 0;
            box-sizing: border-box;
          }
          .template-auth-reader-form-shell form .grid {
            grid-template-columns: minmax(0, 1fr) !important;
          }
          .template-auth-reader-form-shell form .col-span-2 {
            grid-column: auto !important;
            width: 100%;
          }
              .template-auth-reader-form-shell #Register-signup-button {
            width: 100% !important;
          }
          .template-auth-reader-sign-up {
            background: #ffffff !important;
            overflow: visible !important;
          }
          .template-auth-reader-sign-up .academy-signup-page {
            min-height: auto !important;
            margin: 0 !important;
            padding: 0 !important;
            background: transparent !important;
            align-items: stretch !important;
            justify-content: flex-start !important;
          }
          .template-auth-reader-sign-up .academy-signup-card {
            display: block !important;
            width: 100% !important;
            max-width: 100% !important;
            overflow: visible !important;
            border-radius: 0 !important;
            box-shadow: none !important;
          }
          .template-auth-reader-sign-up .academy-signup-info {
            display: none !important;
          }
          .template-auth-reader-sign-up .academy-signup-form {
            width: 100% !important;
            padding: clamp(22px, 4vw, 34px) !important;
          }
          .template-auth-reader-sign-up .academy-signup-fields {
            display: grid !important;
            grid-template-columns: minmax(0, 1fr) !important;
            gap: 16px !important;
          }
          .template-auth-reader-sign-up .academy-signup-field,
          .template-auth-reader-sign-up .academy-signup-terms,
          .template-auth-reader-sign-up .academy-signup-submit {
            min-width: 0 !important;
            width: 100% !important;
            grid-column: 1 / -1 !important;
          }
          .template-auth-reader-sign-up .academy-signup-form label {
            margin-bottom: 7px !important;
            color: #374151 !important;
            font-weight: 700 !important;
          }
          .template-auth-reader-sign-up .academy-signup-form input:not([type="checkbox"]),
          .template-auth-reader-sign-up .academy-signup-form select {
            width: 100% !important;
            min-height: 46px !important;
            border: 1px solid #d1d5db !important;
            border-radius: 8px !important;
            background: #ffffff !important;
            color: #111827 !important;
            font-size: 14px !important;
            line-height: 1.4 !important;
            box-sizing: border-box !important;
          }
          .template-auth-reader-sign-up .academy-signup-form input:not([type="checkbox"]):focus,
          .template-auth-reader-sign-up .academy-signup-form select:focus {
            border-color: ${theme.primary || '#746EAC'} !important;
            box-shadow: 0 0 0 3px rgba(116, 110, 172, 0.16) !important;
            outline: none !important;
          }
          .template-auth-reader-sign-up #Register-phoneNumber-reddittextfield {
            padding-left: 5rem !important;
          }
          .template-auth-reader-sign-up .academy-signup-field div.relative > span {
            left: 0.75rem !important;
            width: 3rem !important;
            justify-content: center !important;
            pointer-events: none !important;
          }
          .template-auth-reader-sign-up .academy-signup-submit #Register-signup-button {
            width: 100% !important;
            min-height: 46px !important;
            border-radius: 10px !important;
            font-size: 15px !important;
          }
          .template-auth-reader-sign-up > div[class*="min-h-screen"] {
            min-height: auto !important;
            margin: 0 !important;
            padding: 0 !important;
            background: transparent !important;
            align-items: stretch !important;
            justify-content: flex-start !important;
          }
          .template-auth-reader-sign-up > div[class*="min-h-screen"] > div[class*="grid"][class*="max-w-7xl"] {
            display: block !important;
            width: 100% !important;
            max-width: 100% !important;
            overflow: visible !important;
            border-radius: 0 !important;
            box-shadow: none !important;
          }
          .template-auth-reader-sign-up > div[class*="min-h-screen"] > div[class*="grid"][class*="max-w-7xl"] > div:first-child {
            display: none !important;
          }
          .template-auth-reader-sign-up form {
            display: block !important;
            padding: clamp(22px, 4vw, 34px) !important;
          }
          .template-auth-reader-sign-up form > div:first-child {
            margin-bottom: 22px !important;
          }
          .template-auth-reader-sign-up form h3 {
            color: #111827 !important;
            font-size: clamp(24px, 3vw, 30px) !important;
            line-height: 1.2 !important;
            margin: 0 !important;
          }
          .template-auth-reader-sign-up form > div[class*="grid"] {
            display: grid !important;
            grid-template-columns: minmax(0, 1fr) !important;
            gap: 18px !important;
          }
          .template-auth-reader-sign-up form label {
            margin-bottom: 7px !important;
            color: #374151 !important;
            font-weight: 700 !important;
          }
          .template-auth-reader-sign-up form input,
          .template-auth-reader-sign-up form select {
            min-height: 46px !important;
            border-radius: 8px !important;
            border-color: #d1d5db !important;
            background: #ffffff !important;
            color: #111827 !important;
            font-size: 14px !important;
            line-height: 1.4 !important;
          }
          .template-auth-reader-sign-up form #Register-phoneNumber-reddittextfield {
            padding-left: 5rem !important;
          }
          .template-auth-reader-sign-up form input[type="checkbox"] {
            width: 16px !important;
            min-width: 16px !important;
            height: 16px !important;
            min-height: 16px !important;
            margin-top: 2px !important;
            padding: 0 !important;
          }
          .template-auth-reader-sign-up form [class*="col-span-2"] {
            grid-column: 1 / -1 !important;
          }
          .template-auth-reader-sign-up #Register-signup-button {
            width: 100% !important;
            min-height: 46px !important;
            border-radius: 10px !important;
            font-size: 15px !important;
          }
          .template-auth-reader-sign-up .academy-signup-field input[type=password],
          .template-auth-reader-sign-up .academy-signup-field input[id*=password i] {
            padding-right: 3.25rem !important;
          }
          .template-auth-reader-sign-up .academy-signup-field .relative > button[type=button] {
            width: 2.75rem !important;
            min-width: 2.75rem !important;
            max-width: 2.75rem !important;
            right: 0.25rem !important;
            padding-left: 0.5rem !important;
            padding-right: 0.5rem !important;
            z-index: 1;
          }
          .template-auth-reader-retail.template-auth-reader-sign-up .academy-signup-field > span,
          .template-auth-reader-retail.template-auth-reader-sign-up .academy-signup-field > span > div {
            display: block !important;
            width: 100% !important;
            min-width: 0 !important;
            max-width: 100% !important;
          }
          .template-auth-reader-retail.template-auth-reader-sign-up .academy-signup-fields,
          .template-auth-reader-retail.template-auth-reader-sign-up form > div[class*=grid] {
            grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
          }
          .template-auth-reader-retail.template-auth-reader-sign-up .academy-signup-fields > .academy-signup-field {
            grid-column: auto !important;
          }
          .template-auth-reader-retail.template-auth-reader-sign-up .academy-signup-terms label {
            display: flex !important;
            align-items: center !important;
            gap: 0.5rem !important;
            margin: 0 !important;
            width: 100% !important;
          }
          .template-auth-reader-retail.template-auth-reader-sign-up .academy-signup-terms label > span {
            display: inline-flex !important;
            align-items: center !important;
            flex-wrap: nowrap !important;
            gap: 0.25rem !important;
            width: auto !important;
            min-width: 0 !important;
          }
          .template-auth-reader-retail.template-auth-reader-sign-up .academy-signup-terms button {
            display: inline !important;
            width: auto !important;
            min-width: 0 !important;
            max-width: none !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          .template-auth-reader-sign-up > div[class*="min-h-screen"] > div[class*="mt-6"][class*="text-center"] {
            margin: 0 !important;
            padding: 18px clamp(22px, 4vw, 34px) 28px !important;
            border-top: 1px solid ${theme.borderColor || '#e5e7eb'} !important;
            background: #f8fafc !important;
          }
          .template-auth-reader-sign-up > div[class*="min-h-screen"] > div[class*="mt-6"][class*="text-center"] p {
            color: #475569 !important;
            margin-top: 8px !important;
          }
          .template-auth-reader-sign-up > div[class*="min-h-screen"] > div[class*="mt-6"][class*="text-center"] a {
            color: ${theme.primary || '#746EAC'} !important;
            font-weight: 800 !important;
          }
          .template-auth-reader-sign-up #googleSignInButton {
            max-width: 100% !important;
          }
          @media (max-width: 1100px) {
            .template-auth-reader-grid {
              gap: 24px !important;
            }
          }
          @media (max-width: 900px) {
            .template-auth-reader-grid {
              grid-template-columns: minmax(0, 1fr) !important;
            }
          }
          @media (max-width: 560px) {
            .template-auth-reader-form-shell > .max-w-md {
              padding: 22px !important;
            }
            .template-auth-reader-retail.template-auth-reader-sign-up .academy-signup-fields,
            .template-auth-reader-retail.template-auth-reader-sign-up form > div[class*=grid] {
              grid-template-columns: minmax(0, 1fr) !important;
            }
            .template-auth-reader-retail.template-auth-reader-sign-up .academy-signup-fields > .academy-signup-field {
              grid-column: 1 / -1 !important;
            }
          }
        `}</style>
      </section>
      {typeof renderSection === 'function'
        ? remainingSections.map((item, index) => renderSection(item, index + 1, template, educationData))
        : null}
    </>
  );
};

export default AuthPageReader;
